// What a traffic notice was for and, for speeding, the two speeds on it
// (db/migrations/0390 and 0481), and where a speeding fine stands on the
// driving conduct ladder (0483).
//
// Works on history rows too: classifying a notice changes none of the figures a
// sheet or a statement carried. Whether it is reckless, whether it counts, and
// the dispute deadline are the database's; this only shows them and sends what
// the fines administrator typed from the notice.
import { useState } from 'react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Chip } from '../interviews/Chip'
import { useSetFineOffence } from '../../hooks/backend/fines'
import { CATEGORY_OPTIONS, CONSENT_LABEL, OUTCOME_LABEL, UNATTRIBUTED_FINE_LABEL, fineSpeedLine, type ConsentStatus, type DisputeOutcome } from '../../utils/drivingFines'
import type { FineDetail } from '../../utils/fines'
import { readableDate } from '../../utils/coachingDates'

const BUCKET_WORDS: Record<string, string> = {
  awaiting_attribution: 'Waiting to be attributed',
  awaiting_response: 'With the driver: dispute window',
  disputed: 'Disputed by the driver',
  counted: 'Confirmed: counts as a breach week',
  reckless: 'Confirmed: reckless driving, goes to a hearing',
  not_counted: 'Not counted',
}

export function FineOffenceSection({ detail, onChanged }: { detail: FineDetail; onChanged: (d: FineDetail) => void }) {
  const f = detail.fine
  const set = useSetFineOffence<FineDetail>()
  const [editing, setEditing] = useState(false)
  const [category, setCategory] = useState(f.offence_category ?? '')
  const [recorded, setRecorded] = useState(f.recorded_kmh?.toString() ?? '')
  const [limit, setLimit] = useState(f.limit_kmh?.toString() ?? '')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  if (f.entry_kind !== 'notice') return null
  const c = detail.conduct

  const save = async () => {
    setError(null)
    try {
      const d = await set.trigger({
        id: f.id,
        offence_category: category || null,
        recorded_kmh: category === 'speeding' ? recorded || null : null,
        limit_kmh: category === 'speeding' ? limit || null : null,
        note: note || null,
      })
      if (d) onChanged(d)
      setEditing(false)
      setNote('')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <section className="space-y-2 border-t border-border px-4 py-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">What it was for</h3>
      {!editing ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span>
            {CATEGORY_OPTIONS.find((o) => o.value === f.offence_category)?.label ?? 'Not classified yet'}
            {f.offence_category === 'speeding' && `: ${fineSpeedLine(f)}`}
          </span>
          {f.offence_category_basis === 'description_rule' && (
            <span className="text-xs text-muted-foreground">(read from the offence text; correct it if wrong)</span>
          )}
          {c?.reckless && <Chip tone="red">Reckless, clause 21.5</Chip>}
          <Button size="sm" variant="outline" className="h-7" onClick={() => setEditing(true)}>
            {f.offence_category ? 'Change' : 'Classify'}
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-end gap-2">
            <NativeSelect className="h-8 w-40 text-xs" aria-label="What the notice was for" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Not classified</option>
              {CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </NativeSelect>
            {category === 'speeding' && (
              <>
                <Input className="h-8 w-28 text-xs" inputMode="numeric" aria-label="Recorded km/h" placeholder="Recorded km/h" value={recorded} onChange={(e) => setRecorded(e.target.value)} />
                <Input className="h-8 w-28 text-xs" inputMode="numeric" aria-label="Limit km/h" placeholder="Limit km/h" value={limit} onChange={(e) => setLimit(e.target.value)} />
              </>
            )}
            <Input className="h-8 max-w-xs text-xs" aria-label="Why it is changing" placeholder="Why (needed once the driver was told)" value={note} onChange={(e) => setNote(e.target.value)} />
            <Button size="sm" className="h-8" disabled={set.loading} onClick={() => void save()}>
              Save
            </Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
          {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}
        </div>
      )}
      {c && (
        <p className="text-xs text-muted-foreground">
          Driving conduct: {BUCKET_WORDS[c.bucket] ?? c.bucket}
          {c.bucket === 'awaiting_attribution' && c.unattributed_reason && `, ${(UNATTRIBUTED_FINE_LABEL[c.unattributed_reason] ?? c.unattributed_reason).toLowerCase()}`}
          {c.response_due_on && c.stage === 'awaiting_response' && `, until ${readableDate(c.response_due_on)}`}
          {c.outcome && `. ${OUTCOME_LABEL[c.outcome as DisputeOutcome] ?? c.outcome}`}
          {c.coaching_only_reason && `. ${c.coaching_only_reason.charAt(0).toUpperCase()}${c.coaching_only_reason.slice(1)}`}
          {c.consent && `. ${CONSENT_LABEL[c.consent.status as ConsentStatus] ?? c.consent.status}`}. The steps are recorded on the Driving
          conduct page, Fines tab.
        </p>
      )}
    </section>
  )
}
